import { IPROP_MAP, modifyProps } from '../../generators/modifyProps';

import { JDSBottomNavigationProps } from './generated';
import { LinkProps } from '../jdslink/logic';
import { MouseEvent } from 'react';
import { useSelected } from '../../hooks/useSelected';

export type IconLinkType = { icon: string | JSX.Element } & Omit<
  LinkProps,
  'children'
>;

export interface BottomNavProps
  extends Omit<JDSBottomNavigationProps, 'items' | 'onChange'> {
  /**
   * List of icons and their links. e.g. `IconLink=
   * [{icon:<IcFavorite />}, {icon:<IcAdd />, href: 'news'}]
   * `
   */
  items: Array<IconLinkType & { disabled?: boolean; label?: string }>;
  /**
   * Current active icon selected. You can overwrite internal active
   * state by passing a new active state.
   */
  activeIndex?: number;
  /**
   * Returns the index of icon when clicked.
   * @param activeIndex
   */
  onChange?: (activeIndex: number) => void;
  /**
   * Shows label below icon if true.
   * Default is false
   */
  showLabel?: boolean;
}

export const PROP_MAP: IPROP_MAP<BottomNavProps> = (
  props = {} as BottomNavProps
) => {
  return {
    items: {
      properties: {
        items: props?.items?.map((i) => ({
          ...i,
          label: i.label || i.title
        }))
      }
    }
  };
};

export const withLogic =
  (Component: React.ComponentType<BottomNavProps>): React.FC<BottomNavProps> =>
  (userProps: BottomNavProps) => {
    const props = modifyProps(userProps, PROP_MAP);
    const { activeIndex, items = [], onChange, ...rest } = props;

    const { index, onClick } = useSelected(activeIndex);

    const navItems: BottomNavProps['items'] = items.map((item, i) => ({
      ...item,
      linkProps: {
        href: item['href'],
        provider: item['provider'],
        target: item['target'],
        newTab: item['newTab']
      },
      onClick: (ev: MouseEvent<HTMLDivElement>) => {
        if (!item.disabled) {
          item['onClick']?.(ev);
          onClick(i);
          onChange?.(i);
        }
      }
    }));

    return (
      <Component
        {...{
          ...rest,
          items: navItems,
          activeIndex: index,
          selectedIndex: index
        }}
      />
    );
  };
